/**
 * Панель «Параметры» на `/generate` — одна на пины и референсы страницы.
 *
 * Пресет только ЗАПОЛНЯЕТ поля (качество, размер, n), дальше правится каждое.
 * `null` в `GenParams` — «решает сервис»: поле показывает значение пресета, правила —
 * весь каталог. Сняли все галочки группы — `[]` (запуск без правил), а не `null`.
 *
 * Промпт всегда на виду, переключатель «Авто | Мой» (`lib/promptMode.ts`). «Авто» — предпросмотр первой пары,
 * собранный сервисом из галочек правил (состояние — `promptView`), только чтение. «Мой» — свой текст вместо задания и
 * правил, хранится в браузере; «Что учесть» и список входов сервис дописывает к нему сам у каждой пары (`tail` —
 * как это выглядит у первой), поэтому «Мой» годится на любое число пар. Галочки правил в «Моём» не действуют.
 */
import { Copy, ScanSearch } from "lucide-react";
import { useState, type ReactNode } from "react";

import { btnPrimary, ErrorLine, label, Segmented } from "@/components/trends/ui";
import { DEFAULT_MODEL, type FittingOptions, type GenParams, type Rule } from "@/lib/fitting";
import { ruleGroup } from "@/features/generate/model/generateForm";
import { usd } from "@/lib/project";
import { modelParams, type PromptMode } from "@/lib/promptMode";
import {
  FOREIGN_SCENE,
  customSceneHint,
  foreignScene,
  previewCaption,
  sceneMissingLine,
  type PromptView,
  type SceneState,
} from "@/lib/promptView";

const AUTO = "auto";

export function ParamsPanel({
  options,
  value,
  onChange,
  kinds,
  portrait,
  preview,
  previewView,
  previewError,
  promptMode,
  onPromptMode,
  customPrompt,
  onCustomPrompt,
  onTakeBase,
  customRef,
  firstRefId,
  tail,
  base,
  scene,
  sceneEstimate,
  sceneCount,
  onAnalyzeScene,
  analyzing,
  analyzeError,
  estimateLine,
  warning,
}: {
  options: FittingOptions;
  value: GenParams;
  onChange: (next: GenParams) => void;
  kinds: { pins: boolean; refs: boolean; manual: boolean };
  /** во входах есть внешность модели: правила про «другую/новую женщину» сервис снимет сам (№317) */
  portrait: boolean;
  /** «Авто» — собранный промпт первой пары */
  preview: string | null;
  previewView: PromptView;
  previewError: unknown;
  promptMode: PromptMode;
  onPromptMode: (mode: PromptMode) => void;
  customPrompt: string;
  onCustomPrompt: (text: string) => void;
  /** «взять из авто»: страница кладёт заготовку и помнит, чей в ней разбор сцены */
  onTakeBase: (text: string) => void;
  /** референс, чей разбор лежит в своём тексте (взят «из авто»); null — неизвестно */
  customRef: number | null;
  /** референс первой пары; null — первая пара не референс */
  firstRefId: number | null;
  /** «Мой»: что сервис допишет к своему тексту у первой пары; null — ещё не собрано */
  tail: string | null;
  /** задание и правила без заметки и списка входов — заготовка «Моего» */
  base: string | null;
  /** разбор сцены первой пары; null — у пары его не бывает (пин, без пары) */
  scene: SceneState;
  sceneEstimate: number | null;
  sceneCount: number;
  onAnalyzeScene: () => void;
  analyzing: boolean;
  analyzeError: unknown;
  estimateLine: ReactNode;
  warning: ReactNode;
}) {
  const preset = options.presets[value.preset];
  const model = options.models.find((m) => m.code === (value.model ?? DEFAULT_MODEL));
  const router = model?.provider === "openrouter";
  const quality = value.quality ?? (router ? (typeof model?.default_params.quality === "string" ? model.default_params.quality : "") : (preset?.quality ?? ""));
  const size = value.size ?? preset?.size ?? AUTO;
  const n = value.n ?? (router ? 1 : (preset?.n ?? 1));
  const custom = promptMode === "custom";
  // «взять из авто» поверх непустого своего текста — вторым нажатием: свой текст не теряется случайно
  const [confirmBase, setConfirmBase] = useState(false);
  const takeBase = () => {
    if (base === null) return;
    if (customPrompt.trim() !== "" && !confirmBase) {
      setConfirmBase(true);
      return;
    }
    setConfirmBase(false);
    onTakeBase(base);
  };
  // «Мой»: пин первой парой и референс второй — разбор у референса есть, подсказка как для готового
  const customHint = customSceneHint(scene === null && kinds.refs ? "ready" : scene, sceneCount);
  const missingLine = scene === "missing" ? sceneMissingLine(custom, sceneCount) : null;
  const foreign = custom && foreignScene(customPrompt, customRef, firstRefId, sceneCount);
  const counts = Array.from({ length: router ? (model?.max_n ?? 1) : options.n_max }, (_, i) => String(i + 1));

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-annot="модель">
        <Segmented
          label="Модель"
          value={value.model ?? DEFAULT_MODEL}
          onPick={(code) => {
            const m = options.models.find((x) => x.code === code);
            if (m && !m.disabled) onChange(modelParams(value, m));
          }}
          options={options.models.map((m) => ({
            value: m.code,
            label: m.label,
            disabled: !!m.disabled,
            ...(m.disabled ? { title: `${m.label}: ${m.disabled}` } : {}),
          }))}
        />
        {model ? (
          <span className={label}>
            {router && model.frame_price !== null ? `≈ ${usd(model.frame_price, 3)} за кадр · ` : ""}
            до {model.max_inputs} {model.max_inputs === 1 ? "входа" : "входов"}
            {options.models.some((m) => m.disabled) ? ` · недоступны: ${options.models.filter((m) => m.disabled).map((m) => `${m.label} (${m.disabled})`).join(", ")}` : ""}
          </span>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {router ? null : (
        <Segmented
          label="Пресет"
          value={value.preset}
          onPick={(code) => {
            const p = options.presets[code];
            onChange(p ? { ...value, preset: code, quality: p.quality, size: p.size, n: p.n } : { ...value, preset: code });
          }}
          options={Object.entries(options.presets).map(([code, p]) => ({ value: code, label: p.label, title: p.note }))}
        />
        )}
        {router ? (
          model?.quality ? (
            <Segmented
              label="Качество"
              value={quality}
              onPick={(q) => onChange({ ...value, quality: q })}
              options={model.quality.map((q) => ({ value: q, label: q }))}
            />
          ) : null
        ) : (
          <Segmented
            label="Качество"
            value={quality}
            onPick={(q) => onChange({ ...value, quality: q })}
            options={options.qualities.map(([code, text]) => ({ value: code, label: text.split(" · ")[0] ?? code, title: text }))}
          />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {router ? (
          <>
            <label className="flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg text-[12.5px]" data-annot="пропорция">
              <span className="flex h-full items-center border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">Пропорция</span>
              <select
                className="h-full bg-bg px-2 text-fg outline-none"
                value={value.aspect ?? AUTO}
                onChange={(e) => onChange({ ...value, aspect: e.target.value })}
              >
                <option value={AUTO}>по пропорции сцены</option>
                {(model?.aspects ?? []).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            {model?.resolution ? (
              <Segmented
                label="Разрешение"
                value={value.resolution ?? ""}
                onPick={(r) => onChange({ ...value, resolution: r })}
                options={model.resolution.map((r) => ({ value: r, label: r }))}
              />
            ) : null}
          </>
        ) : (
          <label className="flex h-8 items-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg text-[12.5px]">
            <span className="flex h-full items-center border-r border-border bg-bg-subtle px-2 text-[11.5px] text-fg-faint">Размер</span>
            <select
              className="h-full bg-bg px-2 text-fg outline-none"
              value={size}
              onChange={(e) => onChange({ ...value, size: e.target.value })}
            >
              <option value={AUTO}>по пропорции сцены</option>
              {options.sizes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        )}
        <Segmented
          label="Кадров на пару"
          value={String(n)}
          onPick={(v) => onChange({ ...value, n: Number(v) })}
          options={counts.map((c) => ({ value: c, label: c }))}
        />
        <Segmented
          label="Вход, px"
          value={String(value.fit_px)}
          onPick={(v) => onChange({ ...value, fit_px: Number(v) })}
          options={options.fit_px.map((px) => ({
            value: String(px),
            label: String(px),
            title: "длинная сторона фото, которые уходят в модель",
          }))}
        />
      </div>
      {kinds.pins ? (
        <RuleGroup
          title="Правила примерки (пины)"
          catalog={options.rules.fitting}
          value={value.rules_fitting}
          onChange={(rules) => onChange({ ...value, rules_fitting: rules })}
          off={custom}
          portrait={portrait}
        />
      ) : null}
      {kinds.refs ? (
        <RuleGroup
          title="Правила генерации по референсу"
          catalog={options.rules.reference}
          value={value.rules_reference}
          onChange={(rules) => onChange({ ...value, rules_reference: rules })}
          off={custom}
          portrait={portrait}
        />
      ) : null}
      {kinds.manual ? (
        <RuleGroup
          title="Правила генерации без сцены"
          catalog={options.rules.manual}
          value={value.rules_manual}
          onChange={(rules) => onChange({ ...value, rules_manual: rules })}
          off={custom}
          portrait={portrait}
        />
      ) : null}
      <div className="grid gap-1 text-[12.5px]" data-annot="промпт">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-fg">Промпт</span>
          <Segmented
            label="Чей"
            annot="промпт: авто или мой"
            value={promptMode}
            onPick={(m) => {
              setConfirmBase(false);
              onPromptMode(m);
            }}
            options={[
              { value: "auto" as const, label: "Авто", title: "сервис собирает из галочек правил" },
              { value: "custom" as const, label: "Мой", title: "свой текст, хранится в этом браузере" },
            ]}
          />
        </div>
        {custom ? (
          <>
            <textarea
              className="min-h-[220px] w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg p-2 font-mono text-[12px] text-fg outline-none focus:border-accent"
              value={customPrompt}
              maxLength={options.prompt_max}
              placeholder="Свой промпт — лучше по-английски: задание и требования. «Что учесть» и список входов сервис допишет сам."
              onChange={(e) => {
                setConfirmBase(false);
                onCustomPrompt(e.target.value);
              }}
            />
            <div className="flex flex-wrap items-center gap-2">
              <span className={label}>
                Сохраняется в этом браузере. Уходит вместо задания и правил, к нему у каждой пары дописываются «Что учесть» и
                список входов — так он работает на любом числе пар.
              </span>
              <button type="button" className={`${btnPrimary} ml-auto`} disabled={base === null} onClick={takeBase}>
                <Copy size={13} />
                {confirmBase ? "заменить мой текст авто?" : "взять из авто"}
              </button>
            </div>
            {customHint ? <span className={label}>{customHint}</span> : null}
            {tail ? (
              <div className="grid gap-1">
                <span className={label}>Сервис допишет (первая пара):</span>
                <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg-subtle p-2 font-mono text-[12px] text-fg-muted">
                  {tail}
                </pre>
              </div>
            ) : previewView === "none" ? (
              <span className={label}>Список входов появится, когда будет сцена или платье с отмеченными фото.</span>
            ) : null}
          </>
        ) : (
          <>
            {previewView === "ready" ? (
              <textarea
                className="min-h-[220px] w-full resize-y rounded-[var(--radius-ui)] border border-border bg-bg-subtle p-2 font-mono text-[12px] text-fg-muted outline-none"
                value={preview ?? ""}
                readOnly
              />
            ) : null}
            <span className={label}>
              {previewView === "none"
                ? "Промпт появится, когда будет из чего собрать: сцена или платье с отмеченными фото."
                : previewView === "loading"
                  ? "собираю…"
                  : previewView === "ready"
                    ? previewCaption(scene, sceneCount)
                    : null}
            </span>
          </>
        )}
        {missingLine !== null && (custom || previewView === "ready") ? (
          <div className="flex flex-wrap items-center gap-2" data-annot="промпт: разбор сцены">
            <span className={label}>{missingLine}</span>
            <button type="button" className={`${btnPrimary} ml-auto`} disabled={analyzing} onClick={onAnalyzeScene}>
              <ScanSearch size={13} />
              {analyzing ? "разбираю…" : `Разобрать сейчас ≈ ${usd(sceneEstimate ?? 0, 2)}`}
            </button>
          </div>
        ) : null}
        <ErrorLine error={analyzeError} />
        {/* и при старом тексте на экране: упавшая пересборка не должна выглядеть вечным «собираю» */}
        <ErrorLine error={previewError} />
      </div>
      <div className="text-[12.5px] text-fg-muted">{estimateLine}</div>
      {warning}
      {foreign ? <div className="text-[12.5px] text-warn">{FOREIGN_SCENE}</div> : null}
    </div>
  );
}

/** Почему галочка погасла: правило снимает сам сервис, пока во входах есть внешность модели (№317). */
const DROP_HINT = "снято: во входах есть внешность модели — эту женщину и возьмёт модель";

function RuleGroup({
  title,
  catalog,
  value,
  onChange,
  off,
  portrait,
}: {
  title: string;
  catalog: Rule[];
  value: string[] | null;
  onChange: (rules: string[] | null) => void;
  /** промпт «Мой»: правила не уходят в модель — галочки видны, но не действуют */
  off: boolean;
  /** во входах есть внешность модели: правила `drop_portrait` гаснут (№317) */
  portrait: boolean;
}) {
  const { chosen, offered, live, dropped } = ruleGroup(catalog, value, portrait);
  // какие коды гасит сервис, решено в `ruleGroup` — здесь только читаем его ответ
  const droppedCodes = new Set(dropped.map((r) => r.code));
  const toggle = (code: string) => {
    const next = catalog.map((r) => r.code).filter((c) => (c === code ? !chosen.includes(c) : chosen.includes(c)));
    // весь каталог — снова «решает сервис»; пустой список остаётся пустым: запуск без правил
    onChange(next.length === catalog.length ? null : next);
  };

  return (
    <fieldset className={`grid gap-1 ${off ? "opacity-50" : ""}`} data-annot={title} disabled={off}>
      <legend className={label}>
        {title} · {off ? "не действуют: промпт «Мой»" : `${live.length} из ${offered.length}`}
      </legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {catalog.map((r) => {
          // снятое сервисом показываем снятым и недоступным, но выбор сотрудника не переписываем:
          // уберут портрет с плитки — галочка вернётся прежней
          const dropped = droppedCodes.has(r.code);
          return (
            <label
              key={r.code}
              title={dropped ? DROP_HINT : r.text}
              className={`inline-flex items-center gap-1.5 text-[12.5px] ${dropped ? "text-fg-faint" : "cursor-pointer text-fg"}`}
            >
              <input
                type="checkbox"
                checked={!dropped && chosen.includes(r.code)}
                disabled={dropped}
                onChange={() => toggle(r.code)}
              />
              {r.label}
              {dropped ? <span className="text-fg-faint">· снято: внешность модели</span> : null}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
